import React, { useState } from 'react';
import { useCustomMutation, useNotification } from '@refinedev/core';
import { Button, Card, Form, Input, Modal, theme } from 'antd';

const MIN_LENGTH = 8;

/**
 * Sets a new password for a member who cannot reset it in the app (10/15, the
 * customer: "add amend password in CMS"). The member is signed out on every
 * device and signs in again with the new password.
 */
export const UserPassword: React.FC<{ userId: string }> = ({ userId }) => {
  const { token } = theme.useToken();
  const { open } = useNotification();
  const [form] = Form.useForm<{ newPassword: string; confirmPassword: string }>();
  const [isOpen, setIsOpen] = useState(false);
  const { mutate, isLoading } = useCustomMutation<{ message: string }>();

  const close = () => {
    setIsOpen(false);
    form.resetFields();
  };

  const save = async () => {
    const { newPassword } = await form.validateFields();
    mutate(
      { url: `/users/${userId}/password`, method: 'put', values: { newPassword } },
      {
        onSuccess: () => {
          open?.({
            type: 'success',
            message: 'Password updated',
            description: 'The member has been signed out and can sign in with the new password.',
          });
          close();
        },
        onError: (error) => {
          open?.({ type: 'error', message: 'Could not update the password', description: error?.message });
        },
      },
    );
  };

  return (
    <Card title="Password" size="small" style={{ marginTop: 24 }}>
      <Button onClick={() => setIsOpen(true)}>Amend password</Button>
      <span style={{ marginLeft: 12, color: token.colorTextSecondary }}>
        For a member who signs in with a phone number or email. They are signed out on all devices.
      </span>
      <Modal
        title="Amend password"
        open={isOpen}
        onOk={save}
        onCancel={close}
        okText="Save"
        confirmLoading={isLoading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="newPassword"
            label="New password"
            rules={[
              { required: true, message: 'Enter a new password' },
              { min: MIN_LENGTH, message: `At least ${MIN_LENGTH} characters` },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm new password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Enter the new password again' },
              ({ getFieldValue }) => ({
                validator: (_, value) =>
                  !value || value === getFieldValue('newPassword')
                    ? Promise.resolve()
                    : Promise.reject(new Error('The passwords do not match')),
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};
